import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { zodResolver } from "@hookform/resolvers/zod";
import { ConfirmationModal, FormGroupContainer, FormModal } from "@packsize/components";
import { Box, Checkbox, FormControlLabel, FormGroup, Grid, TextField, Tooltip, Typography } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import type { MachineType } from "@packsize/schemas/machines";

import { CopyComponent } from "@/components/solution-preview/copy-component";
import { getMachineGroupConfigurationSchema, type MachineGroupConfiguration } from "@/schemas";
import type { Machine } from "@/schemas/machine";

export interface MachineGroupsConfigurationModalTemplateProps {
	readonly open: boolean;
	readonly defaultValues?: MachineGroupConfiguration & { id?: string };
	readonly machineList: Machine[];
	readonly errorMessage?: string;
	readonly onSubmit: (data: MachineGroupConfiguration) => Promise<void>;
	readonly onClose: () => void;
}

const emptyDefaultValues: MachineGroupConfiguration = {
	alias: "",
	description: "",
	configuredMachinesWithMachineType: {},
	enableProductionAutomatically: false,
};

export const MachineGroupsConfigurationModalTemplate: React.FC<MachineGroupsConfigurationModalTemplateProps> = ({
	open,
	defaultValues,
	machineList,
	errorMessage,
	onClose,
	onSubmit,
}) => {
	const intl = useIntl();
	const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);

	const {
		register,
		control,
		handleSubmit,
		reset,
		formState: { errors, isDirty, isSubmitting },
	} = useForm<MachineGroupConfiguration>({
		resolver: zodResolver(getMachineGroupConfigurationSchema(intl)),
		defaultValues: defaultValues ?? emptyDefaultValues,
	});

	useEffect(() => {
		if (open !== false) {
			return;
		}

		reset(defaultValues ?? emptyDefaultValues);
	}, [defaultValues, open, reset]);

	const handleRequestClose = (): void => {
		if (isDirty) {
			setConfirmLeaveOpen(true);
			return;
		}

		onClose();
	};

	return (
		<>
			<ConfirmationModal
				open={confirmLeaveOpen}
				onClose={() => setConfirmLeaveOpen(false)}
				onCancel={() => setConfirmLeaveOpen(false)}
				onConfirm={() => {
					setConfirmLeaveOpen(false);
					onClose();
				}}
				title={intl.formatMessage({ id: "Leave without saving?", defaultMessage: "Leave without saving?" })}
				confirmText={intl.formatMessage({ id: "Leave", defaultMessage: "Leave" })}
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
				size="sm"
				slotProps={{
					confirmButton: {
						"data-testid": "machine-groups-configuration-modal__button__confirm-leave",
						"data-pendoid": "machine-groups-configuration-modal__button__confirm-leave",
					},
					cancelButton: {
						"data-testid": "machine-groups-configuration-modal__button__cancel-leave",
						"data-pendoid": "machine-groups-configuration-modal__button__cancel-leave",
					},
				}}
			>
				<div>
					{intl.formatMessage({ id: "All unsaved work will be lost", defaultMessage: "All unsaved work will be lost" })}
				</div>
			</ConfirmationModal>
			<FormModal
				open={open}
				title={
					defaultValues
						? intl.formatMessage({ id: "Edit Machine Group", defaultMessage: "Edit Machine Group" })
						: intl.formatMessage({ id: "New Machine Group", defaultMessage: "New Machine Group" })
				}
				onSubmit={handleSubmit(onSubmit)}
				size="lg"
				submitText={intl.formatMessage({ id: "Save", defaultMessage: "Save" })}
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
				onClose={handleRequestClose}
				onCancel={handleRequestClose}
				slotProps={{
					confirmButton: {
						loading: isSubmitting,
						"data-testid": "machine-groups-configuration-modal__button__confirm",
						"data-pendoid": "machine-groups-configuration-modal__button__confirm",
					},
					cancelButton: {
						"data-testid": "machine-groups-configuration-modal__button__cancel",
						"data-pendoid": "machine-groups-configuration-modal__button__cancel",
					},
				}}
			>
				<Grid container spacing={4} data-cy="add-form">
					<Grid size={{ xs: 12, md: 5 }}>
						<TextField
							label={intl.formatMessage({ id: "Name", defaultMessage: "Name" })}
							sx={{ mt: 1.3 }}
							fullWidth
							error={Boolean(errors?.alias)}
							helperText={errors?.alias?.message}
							{...register("alias")}
							slotProps={{
								htmlInput: {
									"data-testid": "machine-groups-configuration-modal__input__alias",
									"data-pendoid": "machine-groups-configuration-modal__input__alias",
								},
								formHelperText: {
									"data-testid": "machine-groups-configuration-modal__input__alias-error",
								},
							}}
						/>
						<TextField
							label={intl.formatMessage({ id: "Description", defaultMessage: "Description" })}
							fullWidth
							multiline
							rows={4}
							sx={{ mt: 2 }}
							error={Boolean(errors?.description)}
							helperText={errors?.description?.message}
							{...register("description")}
							slotProps={{
								htmlInput: {
									"data-testid": "machine-groups-configuration-modal__input__description",
									"data-pendoid": "machine-groups-configuration-modal__input__description",
								},
								formHelperText: {
									"data-testid": "machine-groups-configuration-modal__input__description-error",
								},
							}}
						/>
						{Boolean(defaultValues?.id) === true && (
							<TextField
								label={intl.formatMessage({ id: "Machine Group ID", defaultMessage: "Machine Group ID" })}
								fullWidth
								sx={{ mt: 2, "& .MuiInputBase-input": { fontSize: "1rem" } }}
								value={defaultValues?.id ?? ""}
								slotProps={{
									input: {
										readOnly: true,
										endAdornment: <CopyComponent data={defaultValues?.id} />,
									},
									htmlInput: {
										"data-testid": "machine-groups-configuration-modal__input__id",
										"data-pendoid": "machine-groups-configuration-modal__input__id",
									},
								}}
							/>
						)}
						<Typography sx={{ mt: 2 }}>
							<FormattedMessage id="Optional Settings" defaultMessage="Optional Settings" />
						</Typography>
						<Box display="flex" alignItems="center" gap={1}>
							<Controller
								name="enableProductionAutomatically"
								control={control}
								render={({ field }) => (
									<FormControlLabel
										control={
											<Checkbox
												checked={field.value}
												onChange={(e) => field.onChange(e.target.checked)}
												slotProps={{
													input: {
														"data-testid":
															"machine-groups-configuration-modal__checkbox__enable-production-automatically",
														"data-pendoid":
															"machine-groups-configuration-modal__checkbox__enable-production-automatically",
													},
												}}
											/>
										}
										label={
											<FormattedMessage
												id="Enable Production Automatically"
												defaultMessage="Enable Production Automatically"
											/>
										}
									/>
								)}
							/>
							<Tooltip
								title={intl.formatMessage({
									id: "AutoEnableMachineGroupTooltip",
									defaultMessage:
										"When enabled, the Machine Group will automatically be put in play when it comes online",
								})}
							>
								<HelpOutlineIcon sx={{ fontSize: 20, color: "primary.main" }} />
							</Tooltip>
						</Box>
					</Grid>
					<Grid size={{ xs: 12, md: 7 }}>
						<FormGroupContainer
							legend={intl.formatMessage({ id: "Available Machines", defaultMessage: "Available Machines" })}
							sx={{ minHeight: 400, overflowY: "auto" }}
						>
							<Typography variant="body2">
								<FormattedMessage
									id="Select which machines you would like to add to the machine group"
									defaultMessage="Select which machine(s) you would like to add to the machine group"
								/>
							</Typography>
							<Controller
								name="configuredMachinesWithMachineType"
								control={control}
								render={({ field }) => (
									<FormGroup
										sx={{
											display: "grid",
											columnGap: 2,
											gridTemplateColumns: "1fr 1fr",
											mt: 1,
										}}
									>
										{machineList.map((machine) => (
											<FormControlLabel
												key={machine.id}
												control={
													<Checkbox
														checked={Boolean(field.value?.[machine.id])}
														onChange={(e) => {
															if (e.target.checked) {
																field.onChange({
																	...field.value,
																	[machine.id]: machine.machineType as MachineType,
																});
																return;
															}

															const { [machine.id]: _removed, ...rest } = field.value ?? {};
															field.onChange(rest);
														}}
														slotProps={{
															input: {
																"data-testid": `machine-groups-configuration-modal__checkbox__machine-${machine.id}`,
																"data-pendoid": "machine-groups-configuration-modal__checkbox__machine",
															},
														}}
													/>
												}
												label={machine.alias || intl.formatMessage({ id: "Unknown", defaultMessage: "Unknown" })}
											/>
										))}
									</FormGroup>
								)}
							/>
						</FormGroupContainer>
					</Grid>

					{Boolean(errorMessage) === true && (
						<Grid size={12}>
							<Typography
								color="error"
								whiteSpace="pre-wrap"
								data-testid="machine-groups-configuration-modal__save__error"
							>
								{errorMessage}
							</Typography>
						</Grid>
					)}
				</Grid>
			</FormModal>
		</>
	);
};
